import { z } from "zod";

import { userSchema } from "../../user/userSchema";

export enum RoleID {
  Student = 1,
  Teacher = 2,
}
export const USERNAME_MIN_CHARS = 3;
export const USERNAME_MAX_CHARS = 15;
export enum AccountType {
  Kid = "kid_user",
  Standard = "standard",
}

export const adminDashUserSchema = userSchema.extend({
  email: z.email().nullable(),
  role_id: z.enum(RoleID).optional().nullable(),
  subscription_end_date: z.string().optional().nullable(),
  parent_email: z.email().nullable().optional(),
  account_type: z.enum(AccountType).nullable().optional(),
});

export const adminDashMembersSchema = z.object({
  all_user_ids: z.number().array(),
  non_admin_user_ids: z.number().array(),
  results: adminDashUserSchema.array(),
  total: z.number(),
});

/*
 * Originally these schemas extended the adminDashUserSchema, but form validation required too many
 * minute changes to the fields (minimum lengths, everything has to be a string), and trying to extend
 * the original schema with all those changes resulted in some pretty bizarre field orders in the relevant forms.
 */
export const schoolMemberForm = z.object({
  name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.email(),
  role_id: z
    .literal(RoleID.Teacher.toString())
    .or(z.literal(RoleID.Student.toString())),
});

const usernameSchema = z.string().superRefine((val, ctx) => {
  if (val) {
    if (val.length < USERNAME_MIN_CHARS || val.length > USERNAME_MAX_CHARS) {
      ctx.addIssue({
        code: "custom",
        message: "web_username_validation_error_invalid_length",
      });
    }
    // the error message doesn't mention dashes or Unicode letters, but
    // both are present in existing production usernames, so best not to
    // error out on them
    if (/[^a-zA-Z0-9.\p{L}]/gu.test(val)) {
      ctx.addIssue({
        code: "custom",
        message: "web_admin_dash_username_validation",
      });
    }
  }
});

export const newStudentForm = schoolMemberForm
  .pick({ name: true, last_name: true })
  .extend({
    role_id: z.literal(RoleID.Student.toString()),
    login_method: z.enum(["email", "username"]),
    email: z.union([z.email(), z.literal("")]).optional(),
    username: usernameSchema.optional(),
    account_type: z.enum(AccountType),
    parent_email: z.union([z.email(), z.literal("")]).optional(),
  });
export const existingStudentForm = schoolMemberForm
  .pick({ name: true, last_name: true })
  .extend({
    email: z.union([z.email(), z.literal("")]).optional(),
    username: usernameSchema.optional(),
    role_id: z
      .literal(RoleID.Teacher.toString())
      .or(z.literal(RoleID.Student.toString())),
    account_type: z.enum(AccountType),
    parent_email: z.union([z.email(), z.literal("")]).optional(),
    bio: z.string().optional(),
  });

export const existingStudentValidation = schoolMemberForm
  .pick({ name: true, last_name: true })
  .extend({
    username: usernameSchema.optional(),
    bio: z.string().optional(),
    parent_email: z.union([z.email(), z.literal("")]).optional(),
  })
  .and(
    z.union([
      z.object({
        role_id: z.literal("2"), // RoleID.Teacher
        email: z.email(),
        account_type: z.literal("standard"),
      }),
      z.object({
        role_id: z.literal("1"), // RoleID.Student
        email: z.union([z.email(), z.literal("")]).optional(),
        account_type: z.enum(AccountType),
      }),
    ]),
  );

export const schoolMemberImportSchema = z
  .object({
    name: z.string().min(1),
    last_name: z.string().min(1),
    parent_email: z
      .union([
        z.email({ error: "web_admin_dash_parent_email_invalid" }),
        z.literal(""),
      ])
      .optional(),
  })
  .and(
    z.union([
      z.object({
        email: z.email(),
        username: z.never().optional(),
      }),
      z.object({
        email: z.never().optional(),
        username: usernameSchema.optional(),
      }),
    ]),
  );
/**
 * At the moment, this schema is hypothetical and used for display more than anything else. "Regular"
 * users should not be editable in the current state of the admin dashboard.
 */
export const regularMemberForm = z.object({
  name: z.string().min(1),
  last_name: z.string().min(1),
  username: z.string().min(1),
  bio: z.string().optional(),
});

export const adminDashImportStatusSchema = z.object({
  percent_complete: z.number(),
  num_complete: z.number(),
  num_total: z.number(),
  errored_emails: z.string().array().optional().nullable(),
});

export type AdminDashUser = z.infer<typeof adminDashUserSchema>;
export type SchoolMember = z.infer<typeof schoolMemberForm>;
export type SchoolMemberImport = z.infer<typeof schoolMemberImportSchema>;
export type RegularMember = z.infer<typeof regularMemberForm>;
export type AdminDashMembers = z.infer<typeof adminDashMembersSchema>;
